import { useParams, Link as RouterLink } from 'react-router-dom'
import { Box, Button, Grid, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { getCategory, subCategoriesOf } from '@/content/site'
import { productsByCategory } from '@/content/products'
import { layout } from '@/theme'
import { Section } from '@/components/ui/Section'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { Breadcrumbs } from '@/components/ui/Breadcrumbs'
import { ComingSoonMedia } from '@/components/ui/ComingSoonMedia'
import { SubCategoryCard } from '@/components/product/SubCategoryCard'
import { ProductListing } from '@/components/product/ProductListing'
import { ContactCTA } from '@/components/sections/ContactCTA'
import { Seo } from '@/components/seo/Seo'
import { NotFound } from './NotFound'

/**
 * `/products/:category` — shows subcategory cards (Welding Gloves), a product
 * grid (simple categories), or a premium Coming Soon state (no assets yet).
 */
export function CategoryPage() {
  const { category } = useParams()
  const cat = getCategory(category)
  if (!cat) return <NotFound />

  const subs = subCategoriesOf(cat.slug)
  const list = productsByCategory(cat.slug)
  const isEmpty = cat.comingSoon || (subs.length === 0 && list.length === 0)

  const lede = isEmpty
    ? 'This range is currently in development. Contact our team to enquire about availability and specifications.'
    : subs.length > 0
      ? `Choose a ${cat.name} type to explore the range.`
      : `Browse the True Forge ${cat.name} range — engineered for durability, protection and comfort.`

  return (
    <>
      <Seo title={cat.name} path={`/products/${cat.slug}`} description={`Browse the True Forge ${cat.name} range.`} />

      <Section sx={{ pb: 0 }}>
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Products', to: '/products' }, { label: cat.name }]} />
        <Eyebrow>Products</Eyebrow>
        <Typography variant="h1" component="h1" sx={{ maxWidth: 940 }}>
          {cat.name}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 3, maxWidth: layout.contentMaxWidth }}>
          {lede}
        </Typography>
      </Section>

      {isEmpty ? (
        <Section sx={{ pt: 0 }}>
          <Box sx={{ maxWidth: 560 }}>
            <ComingSoonMedia ratio={16 / 9} />
            <Button variant="contained" component={RouterLink} to="/contact" sx={{ mt: 4 }}>
              Enquire About This Range
            </Button>
          </Box>
        </Section>
      ) : subs.length > 0 ? (
        <Section sx={{ pt: 0 }}>
          <Typography variant="h2" component="h2" sx={visuallyHidden}>
            Product ranges
          </Typography>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {subs.map((sub, i) => (
              <Grid key={sub.slug} size={{ xs: 12, sm: 6 }}>
                <SubCategoryCard sub={sub} index={i + 1} />
              </Grid>
            ))}
          </Grid>
        </Section>
      ) : (
        <Section sx={{ pt: 0 }}>
          <ProductListing products={list} />
        </Section>
      )}

      <ContactCTA />
    </>
  )
}
